/**
 * * 新增配置项来配置柱子的边框，允许配置指定一侧的边框
 *
 * @example
 * ```javascript
 * {
 *   type: 'bar',
 *   itemStyle: {
 *     dvBorderWidth: [2, 0, 0, 0],
 *     dvBorderType: 'solid',
 *     dvBorderColor: '#000',
 *   },
 *   data: [
 *     {
 *       itemStyle: {
 *         dvBorderWidth: [2, 0, 0, 0],
 *         dvBorderType: 'solid',
 *         dvBorderColor: '#000',
 *       },
 *     }
 *   ]
 * }
 * ```
 */
import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import { type default as ExtensionAPI } from 'echarts/lib/core/ExtensionAPI';
import { Group, Line, Rect } from 'echarts/lib/util/graphic';
import {
  initProps,
  updateProps,
  removeElementWithFadeOut
} from 'echarts/lib/animation/basicTransition';
import type { RectLayout, BarSeriesModel, BarView, BarDataItemOption } from './type';
import { HALF } from './util';

const POSITIONS = ['top', 'right', 'bottom', 'left'] as const;

function buildItemBorder(
  group: Group,
  itemLayout: RectLayout,
  option: {
    width: Extract<BarDataItemOption['itemStyle']['dvBorderWidth'], unknown[]>;
    color: Extract<BarDataItemOption['itemStyle']['dvBorderColor'], unknown[]>;
    type: Extract<BarDataItemOption['itemStyle']['dvBorderType'], unknown[]>;
  },
  idx: number,
  seriesModel: BarSeriesModel
) {
  const isAnimationEnabled = seriesModel.isAnimationEnabled();
  const [x0, y0, width, height] = [
    Math.min(itemLayout.x, itemLayout.x + itemLayout.width),
    Math.min(itemLayout.y, itemLayout.y + itemLayout.height),
    Math.abs(itemLayout.width),
    Math.abs(itemLayout.height)
  ];

  // eslint-disable-next-line complexity
  option.width.forEach((w, index) => {
    // ! 配置了边框宽度才有效
    if (typeof w === 'undefined' || w <= 0) {
      return;
    }

    const position = POSITIONS[index];
    let x1: number;
    let y1: number;
    let x2: number;
    let y2: number;

    if (position === 'top') {
      [x1, y1, x2, y2] = [x0, y0 + w / HALF, x0 + width, y0 + w / HALF];
    } else if (position === 'right') {
      [x1, y1, x2, y2] = [x0 + width - w / HALF, y0, x0 + width - w / HALF, y0 + height];
    } else if (position === 'bottom') {
      [x1, y1, x2, y2] = [x0, y0 + height - w / HALF, x0 + width, y0 + height - w / HALF];
    } else {
      [x1, y1, x2, y2] = [x0 + w / HALF, y0, x0 + w / HALF, y0 + height];
    }

    const opts = {
      shape: {
        x1,
        y1,
        x2,
        y2
      },
      style: {
        lineWidth: w,
        lineDash: option?.type?.[index] ?? 'solid',
        stroke: option?.color?.[index] ?? '#000'
      },
      z: ((seriesModel.getData().getItemGraphicEl(idx) as Rect)?.z || 0) + 1
    };
    const name = `dvItemBorder-${idx}-${position}`;
    let borderLineEl = group.childOfName(name) as Line & { __dvAlive?: boolean };

    if (!borderLineEl) {
      borderLineEl = new Line({
        name,
        ...opts
      });

      group.add(borderLineEl);

      if (isAnimationEnabled) {
        borderLineEl.attr({ style: { opacity: 0 } });

        initProps(borderLineEl, { style: { opacity: 1 } }, seriesModel);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (isAnimationEnabled) {
        updateProps(borderLineEl, opts, seriesModel);
      } else {
        borderLineEl.attr(opts);
      }
    }

    borderLineEl.__dvAlive = true;
  });
}

function renderItemBorder(seriesModel: BarSeriesModel, api: ExtensionAPI) {
  const data = seriesModel.getData();
  const { dvBorderWidth, dvBorderColor, dvBorderType } = seriesModel.get('itemStyle') || {};
  const seriesView = api.getViewOfSeriesModel(seriesModel) as BarView;
  let itemBorderGroup = seriesView.__dvItemBorderGroup;
  if (!itemBorderGroup) {
    itemBorderGroup = new Group({ name: 'dvItemBorder' });
    seriesView.__dvItemBorderGroup = itemBorderGroup;
    seriesView.group.add(itemBorderGroup);
  }

  itemBorderGroup.eachChild((childEl: Line & { __dvAlive: boolean }) => {
    childEl.__dvAlive = false;
  });

  // add & update
  data.each(idx => {
    let {
      dvBorderWidth: dvBorderWidthInner = dvBorderWidth,
      dvBorderColor: dvBorderColorInner = dvBorderColor,
      dvBorderType: dvBorderTypeInner = dvBorderType
    } = data.getItemModel<BarDataItemOption>(idx).get('itemStyle') || {};
    // ! 配置了边框宽度才有效
    if (typeof dvBorderWidthInner === 'undefined') {
      return;
    }

    // top / right / bottom / left
    if (!Array.isArray(dvBorderWidthInner)) {
      dvBorderWidthInner = [
        dvBorderWidthInner,
        dvBorderWidthInner,
        dvBorderWidthInner,
        dvBorderWidthInner
      ];
    }
    if (!Array.isArray(dvBorderColorInner)) {
      dvBorderColorInner = [
        dvBorderColorInner,
        dvBorderColorInner,
        dvBorderColorInner,
        dvBorderColorInner
      ];
    }
    if (!Array.isArray(dvBorderTypeInner)) {
      dvBorderTypeInner = [
        dvBorderTypeInner,
        dvBorderTypeInner,
        dvBorderTypeInner,
        dvBorderTypeInner
      ];
    }

    buildItemBorder(
      itemBorderGroup,
      data.getItemLayout(idx) as RectLayout,
      { width: dvBorderWidthInner, color: dvBorderColorInner, type: dvBorderTypeInner },
      idx,
      seriesModel
    );
  });

  // remove
  itemBorderGroup
    .children()
    .slice()
    .forEach((childEl: Line & { __dvAlive: boolean }) => {
      if (!childEl.__dvAlive) {
        removeElementWithFadeOut(childEl, seriesModel);
      }
    });
}

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('series:afterupdate', (ecModal, api) => {
    ecModal.eachSeriesByType('bar', (seriesModel: BarSeriesModel) =>
      renderItemBorder(seriesModel, api)
    );
  });
}
